/**
 * Confirm before any AI the owner starts (P-5.12, decision 11): the price on the button, one
 * dialog, a tick, and a receipt afterwards. One composable for every AI button in the
 * section; mount `components/AiConfirmDialog.vue` with the object it returns.
 *
 * NOTHING HERE IS A SECURITY CONTROL, and that is what lets it stay simple. Every paid route
 * answers 409 `confirm_required` and runs nothing unless the request carries the exact price
 * in credits. A stale price, a lost tick or a second tab can cost the owner one more dialog,
 * never an unconfirmed charge.
 *
 * THE SESSION (O-11, field report F3.5), for Test it and meaning search only. The first press
 * asks in the session wording; later presses show the price on the button and go straight
 * through, still sending the header. The OK is `{ accountId, credits, lastUsedAt }` in this
 * browser tab's sessionStorage, one entry per action, so a reload or leaving the page and coming
 * back does not ask again. It ends on an account switch (and an entry naming another account is
 * never used), after 30 minutes without a use, when the price changes, and whenever the server
 * answers a quote. Storage that throws (a private window, blocked site data) keeps it in this
 * page's memory instead, which is how it worked before.
 */
import { computed, onBeforeUnmount, reactive, ref, unref, watch } from 'vue';

import { useWasloI18n } from '../i18n.js';
import { wasloApi } from '../api/client.js';
import { creditsPhrase, priceFor, receiptPhrase } from '../helpers/ai-price.js';

export const SESSION_IDLE_MS = 30 * 60 * 1000;
const PRICE_TTL_MS = 60 * 1000;
const MAX_QUOTES = 3;

/** One sessionStorage entry per action: Test it and meaning search are separate sessions. */
export const ackStorageKey = op => `waslo.aiConfirm.${op}`;

/** Per account, shared by every button of the page: four Suggest links cost one read. */
const cache = new Map();

/** For specs: start a case from a cold price table. */
export const resetAiPriceCache = () => cache.clear();

const loadCredits = (accountId, force) => {
  const key = String(accountId);
  const hit = cache.get(key);
  if (!force && hit && Date.now() - hit.at < PRICE_TTL_MS) return hit.promise;
  const promise = Promise.resolve()
    .then(() => wasloApi.getCredits(accountId))
    .catch(error => {
      cache.delete(key);
      throw error;
    });
  cache.set(key, { at: Date.now(), promise });
  return promise;
};

/** The stored OK, or null when there is none, it is not ours to read, or storage throws. */
const readStoredAck = op => {
  try {
    const raw = window.sessionStorage.getItem(ackStorageKey(op));
    if (raw === null) return null;
    const ack = JSON.parse(raw);
    return typeof ack?.accountId === 'string' &&
      Number.isSafeInteger(ack.credits) &&
      Number.isFinite(ack.lastUsedAt)
      ? {
          accountId: ack.accountId,
          credits: ack.credits,
          lastUsedAt: ack.lastUsedAt,
        }
      : null;
  } catch {
    return null;
  }
};

const writeStoredAck = (op, ack) => {
  try {
    if (ack === null) window.sessionStorage.removeItem(ackStorageKey(op));
    else window.sessionStorage.setItem(ackStorageKey(op), JSON.stringify(ack));
  } catch {
    // Storage refused: the page's own copy still covers this visit.
  }
};

/**
 * @param {{ accountId: import('vue').Ref<number|null>, op: string, session?: boolean }} options
 *   `op` is one of `helpers/ai-price.js`'s `AI_OP_REASON` keys.
 */
export const useAiConfirm = ({ accountId, op, session = false }) => {
  const { t, locale } = useWasloI18n();
  const prices = ref(null);
  const balance = ref(null);
  /** `{ charged: number|null }` after a run, `null` before one. */
  const receipt = ref(null);
  const dialog = reactive({
    open: false,
    credits: null,
    balance: null,
    priceChanged: false,
    ticked: false,
  });
  /** This page's copy of the OK, and the whole of it when storage throws. */
  let ack = null;
  let waiting = null;

  const accountKey = () => String(unref(accountId) ?? '');
  const currentAck = () => (session ? (readStoredAck(op) ?? ack) : ack);
  const setAck = next => {
    ack = next;
    if (session) writeStoredAck(op, next);
  };

  const refresh = async (force = false) => {
    const id = unref(accountId);
    if (id === null || id === undefined) return;
    try {
      const credits = await loadCredits(id, force);
      prices.value = credits?.prices ?? null;
      balance.value =
        typeof credits?.balance === 'number' ? credits.balance : null;
    } catch {
      // No price shown is safe: the call goes without a header and the server quotes.
      prices.value = null;
    }
  };

  const endSession = () => setAck(null);

  refresh();
  watch(
    () => unref(accountId),
    () => {
      endSession();
      refresh();
    }
  );

  const priceOf = (count = 1) => priceFor(prices.value, op, count);
  const price = computed(() => priceOf(1));
  const creditsText = value => creditsPhrase(t, locale.value, value);

  /** "{action} · {credits}", or the bare action while the price is not known. */
  const label = (action, count = 1) => {
    const value = priceOf(count);
    return value === null
      ? action
      : t('WASLO.AI_CONFIRM.PRICED', { action, credits: creditsText(value) });
  };

  const receiptText = computed(() =>
    receipt.value === null
      ? ''
      : receiptPhrase(t, locale.value, receipt.value.charged)
  );

  const sessionLive = credits => {
    const held = currentAck();
    return (
      held !== null &&
      held.accountId === accountKey() &&
      held.credits === credits &&
      Date.now() - held.lastUsedAt < SESSION_IDLE_MS
    );
  };

  const remember = credits => {
    if (session)
      setAck({ accountId: accountKey(), credits, lastUsedAt: Date.now() });
  };

  const settleWaiting = answer => {
    const pending = waiting;
    waiting = null;
    dialog.open = false;
    if (pending) pending(answer);
  };

  const ask = ({ credits, available, priceChanged }) => {
    settleWaiting(false);
    Object.assign(dialog, {
      open: true,
      credits,
      balance: available,
      priceChanged,
      ticked: false,
    });
    return new Promise(resolve => {
      waiting = resolve;
    });
  };

  const setTicked = value => {
    dialog.ticked = value === true;
  };
  /** Refused without the tick, whatever reached it. */
  const confirm = () => {
    if (dialog.open && dialog.ticked) settleWaiting(true);
  };
  const cancel = () => settleWaiting(false);

  /**
   * Ask (unless a live session already covers this price), then `run(credits)`, which must
   * send `withConfirm(credits)`. A 409 quote clears the session and asks again with the
   * server's number. Resolves `{ cancelled: true }` on Cancel (nothing was sent), or
   * `{ cancelled: false, result }`; rejects with the run's own error otherwise, after setting
   * the receipt to "Nothing was charged." (every failure arm on the server charges 0).
   */
  const confirmAndRun = async ({ count = 1, run }) => {
    receipt.value = null;
    await refresh();
    let credits = priceOf(count);
    if (credits !== null && !(session && sessionLive(credits))) {
      endSession();
      const agreed = await ask({
        credits,
        available: balance.value,
        priceChanged: false,
      });
      if (!agreed) return { cancelled: true };
      remember(credits);
    }

    for (let quotes = 0; quotes < MAX_QUOTES; quotes += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const result = await run(credits);
        receipt.value = {
          charged: Number.isSafeInteger(result?.charged)
            ? result.charged
            : null,
        };
        const held = currentAck();
        if (held !== null) setAck({ ...held, lastUsedAt: Date.now() });
        refresh(true);
        return { cancelled: false, result };
      } catch (error) {
        const quote = error?.quote;
        if (!quote || !Number.isSafeInteger(quote.credits)) {
          receipt.value = { charged: 0 };
          throw error;
        }
        endSession();
        // eslint-disable-next-line no-await-in-loop
        const agreed = await ask({
          credits: quote.credits,
          available: quote.balance,
          priceChanged: credits !== null,
        });
        if (!agreed) return { cancelled: true };
        credits = quote.credits;
        remember(credits);
      }
    }
    receipt.value = { charged: 0 };
    throw new Error(t('WASLO.AI_CONFIRM.PRICE_KEEPS_CHANGING'));
  };

  // Leaving the page settles an open dialog as "no", and KEEPS the session (F3.5).
  onBeforeUnmount(() => {
    cancel();
  });

  return {
    op,
    session,
    price,
    priceOf,
    label,
    creditsText,
    receipt,
    receiptText,
    dialog,
    setTicked,
    confirm,
    cancel,
    confirmAndRun,
    endSession,
    refresh,
  };
};

export default useAiConfirm;
